import {isEmpty, isNil, union} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {AuditCategoryOfStructure} from '../../../types/AuditCategoryOfStructure';
import {AuditParamDefinition} from '../../../types/AuditParamDefinition';
import {AuditParamValue} from '../../../types/AuditParamValue';
import {TConditionFormValue} from '../../../types/ConditionTypes';
import {AuditCategoryUniqueKey} from '../types/AuditCategoryUniqueKey';
import {AuditParamType} from '../types/AuditParamType';
import {getFormFieldName} from './getFormFieldName';
import {getParsedValue} from './getParsedValue';

/**
 * A nested param is only rendered while its parent holds the value that owns it, so it can be
 * required only under the same condition.
 */
export type RequiredParamCondition = {
  fieldName: string;
  value: TConditionFormValue;
};

export type RequiredParam = {
  categoryIds: string[];
  /**
   * The same param definition can be reached through more than one path - two values of the same
   * parent may both reveal it, or it may sit both directly in a category and under a value. Every
   * path contributes its own chain and the param is required as soon as any one of them holds, so
   * an empty chain means "required unconditionally".
   */
  conditionChains: RequiredParamCondition[][];
};

export type RequiredParams = {
  fields: Record<string, RequiredParam>;
  assets: Record<string, RequiredParam>;
  videos: Record<string, RequiredParam>;
};

type RequiredParamBucket = keyof RequiredParams;

const UNCONDITIONAL_CHAINS: RequiredParamCondition[][] = [[]];

/**
 * The value CategoryCard lifts into the card switch - it renders the content of that value only.
 */
const LIFTED_TOGGLE_VALUE = 'true';

/**
 * Every param type names the storage its value is validated against, so that adding a member to
 * AuditParamType cannot silently turn it into a required form field - the build breaks until the
 * new type is classified here. `null` marks the types that are never validated: a switch left off
 * and an unchecked pill set are valid answers, and a label is not an input at all, so requiring
 * any of them would make the inspection impossible to submit.
 * Videos live in audit.videoAssets, so they can be validated neither as fields nor as assets.
 */
const PARAM_TYPE_BUCKET: Record<AuditParamType, RequiredParamBucket | null> = {
  [AuditParamType.TOGGLE]: null,
  [AuditParamType.PILL_CHECKBOX]: null,
  [AuditParamType.ADDITIONAL_LABEL]: null,
  // Recorded by the CarAudit app during the test drive, never entered in the inspection form.
  [AuditParamType.TEST_DRIVE_ROUTE]: null,
  [AuditParamType.PHOTO]: 'assets',
  [AuditParamType.PHOTO_FULLWIDTH]: 'assets',
  [AuditParamType.MORE_PHOTOS]: 'assets',
  [AuditParamType.ADDITIONAL_PHOTOS]: 'assets',
  [AuditParamType.VIDEO]: 'videos',
  [AuditParamType.COUNTER]: 'fields',
  [AuditParamType.DROPDOWN]: 'fields',
  [AuditParamType.RADIO_BUTTONS]: 'fields',
  [AuditParamType.CHECKBOX_BUTTON]: 'fields',
  [AuditParamType.PILL_RADIO]: 'fields',
  [AuditParamType.RATING]: 'fields',
  [AuditParamType.TEXT]: 'fields',
  [AuditParamType.ADDITIONAL_COMMENT]: 'fields',
  [AuditParamType.NUMBER]: 'fields',
  [AuditParamType.DATE]: 'fields',
  [AuditParamType.DATE_YEAR]: 'fields',
  [AuditParamType.YEAR]: 'fields',
  [AuditParamType.SELECTABLE_BUTTON]: 'fields',
  [AuditParamType.CATEGORY_STATE]: 'fields',
  [AuditParamType.ENHANCED_COMMENT]: 'fields',
  [AuditParamType.MILEAGE]: 'fields',
  [AuditParamType.AVERAGE_SPEED]: 'fields',
  [AuditParamType.MAXIMUM_SPEED]: 'fields',
  [AuditParamType.START_TIME]: 'fields',
  [AuditParamType.TEST_DRIVE_DATE]: 'fields',
  [AuditParamType.TIME_DURATION]: 'fields',
  [AuditParamType.END_TIME]: 'fields',
  [AuditParamType.AUTHOR]: 'fields',
  [AuditParamType.GENERAL_COMMENT]: 'fields',
  [AuditParamType.WEAK_GPS_SIGNAL]: 'fields',
  [AuditParamType.DEFAULT_OPTION]: 'fields',
};

/**
 * AuditParamDefinition carries the type as a plain string, so a type the backend adds before the
 * frontend knows about it keeps the previous behaviour of being validated as a form field.
 */
const DEFAULT_BUCKET: RequiredParamBucket = 'fields';

const isKnownParamType = (type: string): type is AuditParamType => type in PARAM_TYPE_BUCKET;

const getRequiredParamBucket = (type: string) =>
  isKnownParamType(type) ? PARAM_TYPE_BUCKET[type] : DEFAULT_BUCKET;

/**
 * AdditionalInformation renders at most one comment and one photo param, both picked with `find`,
 * so a second entry of either type - or an entry of any other type - is never put on screen and
 * must not be required.
 */
const RENDERED_ADDITIONAL_INFORMATION_TYPES = [
  AuditParamType.ADDITIONAL_COMMENT,
  AuditParamType.ADDITIONAL_PHOTOS,
];

const getRenderedAdditionalInformation = (
  additionalInformation: AuditParamDefinition[] | null | undefined
): AuditParamDefinition[] =>
  RENDERED_ADDITIONAL_INFORMATION_TYPES.map((renderedType) =>
    additionalInformation?.find(({type}) => type === renderedType)
  ).filter(isNotNil);

const getNestedParamDefinitions = ({content}: AuditParamValue): AuditParamDefinition[] => [
  ...(content?.relatedActions ?? []),
  ...getRenderedAdditionalInformation(content?.additionalInformation),
];

/**
 * CategoryCard lifts the category's first toggle into the card switch and renders the content of
 * its `'true'` value only, while filtering every toggle out of the related actions it renders - so
 * a second toggle of the same category puts nothing on screen at all. A toggle nested deeper is
 * rendered by AuditFormField, which puts every value's content behind a Condition, so there all
 * values stay reachable.
 */
const getReachableValues = (
  {id, type, values}: AuditParamDefinition,
  liftedToggleId: string | undefined
): AuditParamValue[] => {
  if (type !== AuditParamType.TOGGLE || isNil(liftedToggleId)) {
    return values ?? [];
  }

  if (id !== liftedToggleId) {
    return [];
  }

  return (values ?? []).filter(({value}) => value === LIFTED_TOGGLE_VALUE);
};

const addRequiredParam = (
  bucket: Record<string, RequiredParam>,
  paramName: string,
  requiredParam: RequiredParam
) => {
  const alreadyCollected = bucket[paramName];

  if (isNil(alreadyCollected)) {
    bucket[paramName] = requiredParam;
    return;
  }

  const conditionChains = [...alreadyCollected.conditionChains, ...requiredParam.conditionChains];

  bucket[paramName] = {
    categoryIds: union(alreadyCollected.categoryIds, requiredParam.categoryIds),
    // A path that requires the param unconditionally outranks every conditional one
    conditionChains: conditionChains.some(isEmpty) ? UNCONDITIONAL_CHAINS : conditionChains,
  };
};

// eslint-disable-next-line max-params -- Pre-existing violation
const getRequiredParamsFromParamDefinitions = (
  params: RequiredParams,
  paramDefinitions: AuditParamDefinition[],
  categoryIdsTree: Record<string, string>,
  categoryId: string,
  conditions: RequiredParamCondition[] = [],
  liftedToggleId?: string
): RequiredParams => {
  paramDefinitions.forEach((paramDefinition) => {
    const {id, type, mandatory} = paramDefinition;

    if (mandatory) {
      const bucket = getRequiredParamBucket(type);

      if (isNotNil(bucket)) {
        const paramName = getFormFieldName(
          type === AuditParamType.PHOTO_FULLWIDTH ? categoryIdsTree[0] : categoryId,
          id
        );

        addRequiredParam(params[bucket], paramName, {
          categoryIds: Object.values(categoryIdsTree),
          conditionChains: [conditions],
        });
      }
    }

    // Params nested under a value are rendered once the parent holds that value, so they inherit
    // its condition on top of the conditions of their own ancestors
    getReachableValues(paramDefinition, liftedToggleId).forEach((value) => {
      const nestedParamDefinitions = getNestedParamDefinitions(value);

      if (nestedParamDefinitions.length === 0) {
        return;
      }

      getRequiredParamsFromParamDefinitions(
        params,
        nestedParamDefinitions,
        categoryIdsTree,
        categoryId,
        [
          ...conditions,
          {
            fieldName: getFormFieldName(categoryId, id),
            // The same expression AuditFormField hands to Condition when it renders the nested
            // params, so a param is required exactly when it is on screen. Note that audit.fields is
            // parsed by its own copy of getParsedValue in reducerUtils, which keeps numbers as
            // strings - a condition on a numeric value therefore matches neither here nor in
            // Condition, so the two sides stay in step rather than being individually correct.
            value: getParsedValue(value.value) ?? value.value,
          },
        ]
      );
    });
  });

  return params;
};

export const getRequiredFieldsFromCategories = (
  categories?: AuditCategoryOfStructure[],
  params: RequiredParams = {
    fields: {},
    assets: {},
    videos: {},
  },
  categoryIdsTree: Record<string, string> = {},
  level = 0
): RequiredParams => {
  categories?.forEach((category) => {
    if (
      !category.visible ||
      category.uniqueKey === AuditCategoryUniqueKey.OVERALL_COMMENT ||
      category.uniqueKey === AuditCategoryUniqueKey.TESTDRIVE
    ) {
      return;
    }

    // Drop ids left by a previously processed sibling subtree, so the tree holds only ancestors
    Object.keys(categoryIdsTree).forEach((treeLevel) => {
      if (Number(treeLevel) > level) {
        delete categoryIdsTree[treeLevel];
      }
    });

    categoryIdsTree[level] = category.id;

    const {relatedActions, additionalInformation} = category.paramDefinitions ?? {};
    const liftedToggleId = relatedActions?.find(({type}) => type === AuditParamType.TOGGLE)?.id;

    getRequiredParamsFromParamDefinitions(
      params,
      relatedActions ?? [],
      categoryIdsTree,
      category.id,
      [],
      liftedToggleId
    );

    getRequiredParamsFromParamDefinitions(
      params,
      getRenderedAdditionalInformation(additionalInformation),
      categoryIdsTree,
      category.id
    );

    if (category.childCategories) {
      params = getRequiredFieldsFromCategories(
        category.childCategories,
        params,
        categoryIdsTree,
        level + 1
      );
    }
  });

  return params;
};
